import { useQuery } from '@tanstack/react-query';
import { apiBatch, unwrapBatch } from '@/api/batch';
import type {
    ProfileResponse,
    UserSettingsOptionsResponse,
} from '@/api/types';
import { resolveApiPath } from '@/contexts/GoApiContext';

/**
 * Hook to fetch user profile (with settings) and form options in a single batch request.
 * This minimizes API calls and Lambda invocations.
 *
 * Supported currencies are deliberately not batched here: they are static reference data
 * already loaded once per session by useSupportedCurrencies().
 */
export function useBatchUserSettings() {
    return useQuery({
        queryKey: ['batch-user-settings'],
        queryFn: async () => {
            const result = await apiBatch<{
                profile: ProfileResponse;
                options: UserSettingsOptionsResponse;
            }>([
                { id: 'profile', path: resolveApiPath('/api/profile/me') },
                { id: 'options', path: '/api/user-settings/options' },
            ]);

            return {
                profile: unwrapBatch(result.responses.profile, 'profile'),
                options: unwrapBatch(result.responses.options, 'options'),
            };
        },
    });
}
